import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import useCallbackRef from 'hooks/useCallbackRef';
import ChatFilesContext from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/providers/ChatFiles/ChatFiles.context';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import { getIsArrayEmpty } from 'utils';
import ChatFileRow from './components/ChatFileRow';
import ChatFileRowPlaceholder from './components/ChatFileRowPlaceholder';
import ChatFilesModal from './components/ChatFilesModal';
import { PREVIEWED_FILES_COUNT } from './utils';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';
import { SIZE } from 'models/Size';

import './MessageFiles.styles.scss';

type MessageFilesProps = {
  className?: string;
  dataRoomId: string;
  dataRoomName: string;
  ownerId: string;
  userSide: QAndASides;
  /**
   * Data Room access is a property of the user, not of the side they are on — a sell-side
   * colleague can be without it just as a buyer can. Without it there is no file status to
   * show and nothing to save to.
   */
  hasDataRoomAccess?: boolean;
};

const MessageFiles: React.FC<MessageFilesProps> = (props) => {
  const {
    className,
    dataRoomId,
    dataRoomName,
    ownerId,
    userSide,
    hasDataRoomAccess,
  } = props;

  const classes = classNames('message-files', className);

  const { t } = useTranslation();

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const { files, totalFiles, loading, openFilePreview } =
    useContext(ChatFilesContext);

  const previewedFiles = useMemo(
    () => files.slice(0, PREVIEWED_FILES_COUNT),
    [files],
  );

  const isEmpty = getIsArrayEmpty(previewedFiles);

  // the heading stays put while the first page lands, so the panel doesn't claim the chat
  // has no files before we know that
  const isLoadingFirstPage = loading && isEmpty;

  return (
    <div className={classes}>
      <div className="message-files__heading">
        <p className="message-files__title">
          {t('filesInChat')}
          {!!totalFiles && (
            <Counter
              count={totalFiles}
              size={SIZE.SMALL}
              variant={COUNTER_VARIANTS.GREY}
            />
          )}
        </p>

        {/* with nothing in the chat the description has nothing to describe, so the
            empty message takes its place rather than stacking on top of it */}
        {!isLoadingFirstPage && (
          <p className="message-files__description">
            {isEmpty
              ? t('noFilesYet')
              : hasDataRoomAccess
              ? t('filesInChatDescription')
              : t('filesInChatDescriptionNoDataRoom')}
          </p>
        )}
      </div>

      {isLoadingFirstPage ? (
        // placeholders rather than a loader, so the panel settles into the rows it is
        // about to show instead of jumping from a spinner to a list
        <ul className="message-files__files">
          {Array.from({ length: PREVIEWED_FILES_COUNT }).map((_, index) => (
            <ChatFileRowPlaceholder key={index} />
          ))}
        </ul>
      ) : (
        !isEmpty && (
          <>
            <ul className="message-files__files">
              {previewedFiles.map((chatSessionFile) => (
                <ChatFileRow
                  key={chatSessionFile._id}
                  chatSessionFile={chatSessionFile}
                  hasDataRoomAccess={hasDataRoomAccess}
                  dataRoomId={dataRoomId}
                  dataRoomName={dataRoomName}
                  ownerId={ownerId}
                  userSide={userSide}
                  onOpen={openFilePreview}
                />
              ))}
            </ul>

            {totalFiles > PREVIEWED_FILES_COUNT && (
              <Button
                variant="link"
                className="message-files__view-all"
                onClick={modal?.open}
              >
                {t('viewAllFiles', { count: totalFiles })}
              </Button>
            )}
          </>
        )
      )}

      <ChatFilesModal
        ref={modalRef}
        hasDataRoomAccess={hasDataRoomAccess}
        dataRoomId={dataRoomId}
        dataRoomName={dataRoomName}
        ownerId={ownerId}
        userSide={userSide}
      />
    </div>
  );
};

export default MessageFiles;
